import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { makeLinkPreviewReadyHandler } from '../../components/common/chat/timeline/linkPreviewRealtime';
import { buildChatDropPlan } from '../../components/common/chat/composer/chatDropPlan';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useArtistEventChatRuntime(context) {
  const {
    chatTopics,    ADMIN_MESSAGE_FORMATS,
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    EventNoteActionButton,
    MAX_FILE_BYTES,
    MAX_IMAGE_COUNT,
    MESSAGE_FILE_PATH,
    SOCKET_EVENTS,
    STALE_MS,
    adminLookup,
    appendVariant,
    bannerTimeoutRef,
    buildAssetUrl,
    buildEventAnnouncementChecks,
    buildMessageImageUrl,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    buildRealtimeEventTextMessage,
    bumpEventToTop,
    castingInfoMap,
    chatContainerRef,
    cleanupTimeoutRefs,
    createEventAvatarProfileTargetResolver,
    createEventAvatarRenderers,
    createEventMessageRenderPipeline,
    createEventPipelineOptions,
    createEventProfilePayloadBuilders,
    effectiveGroupEvents,
    ellipsizeFileName,
    fatalAccessError,
    fetchMessages,
    fetchMessagesRef,
    fetchNewMessages,
    fetchNewMessagesRef,
    fetchSidebarRow,
    formatAdminDisplayName,
    formatExpireDateLabel,
    getAnnouncementIconClass,
    getEventComposite,
    getFileIconClass,
    getMainPartLabelRef,
    handleDownload,
    handleMentionClick,
    hasMore,
    hasNewerMessages,
    isAnnouncementMessage,
    isEventFinishedMessage,
    isEventImagesAddedMessage,
    isEventInfoPublishedMessage,
    isEventInfoUpdatedMessage,
    isEventMaterialSharedMessage,
    isEventMembersPublishedMessage,
    isFetching,
    isNearTopForPaging,
    isNoteCreateMessage,
    isNoteDeleteMessage,
    isNoteMessage,
    isNoteUpdateMessage,
    isReadOnly,
    isSearchModeRef,
    isSelectedGroupUnified,
    isSendLocked,
    isSimpleAnnouncementMessage,
    isStageSeatingSharedMessage,
    isTimetableCreatedMessage,
    isTimetableMemoMessage,
    isTimetableRuntimeMessage,
    isTimetableUpdatedMessage,
    isUploading,
    isViewingLatest,
    lastDiffFetchRef,
    linkifyText,
    loadNewerMessages,
    loadingNewerMessages,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messages,
    messagesRef,
    normalizeEventUuidList,
    normalizePartLabel,
    normalizeServerMessage,
    notesModeRef,
    nowJstDbString,
    offset,
    openBanner,
    openImageModal,
    openProfileTarget,
    parseEventTimetableRuntimePayload,
    parsePartIds,
    participantArtistMap,
    participantSnapshot,
    partsLookup,
    patchMessageReadCountsByIds,
    pickFirstString,
    postEventReadReceipt,
    reconcileRecentMessages,
    reconcileTimerRef,
    refreshAnnouncements,
    refreshEventChatUnread,
    refreshMessageReactionDetails,
    refreshPendingMentionTargets,
    refreshTopTimerRef,
    removeMessages,
    replyDraft,
    requestRefreshTop,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveEventTimetableRuntimeVisibility,
    resolveThreadIdByEvent,
    saveLast,
    selectedEventId,
    selectedEventIdRef,
    selectedEventInfo,
    selectedThreadIdRef,
    sendEventChatFileMessage,
    sendEventChatImageMessages,
    setEvents,
    setIsUploading,
    setMessages,
    setOffset,
    setReplyDraft,
    setShowAlbumModal,
    setShowMaterialsModal,
    setShowNewMsgBanner,
    setShowNotes,
    setSidebarDetails,
    setUnreadMap,
    shouldMarkLatestAsRead,
    shouldShowEventMessageSourceBadge,
    showNotes,
    showNotesRef,
    sidebarTimeLabelJST,
    socket,
    statusToDisplayMessage,
    toNum,
    toastError,
    updateSidebarAfterSend,
    upsertEventRowAtTop,
    useChatDragDrop,
  } = context;
    // ===== スクロール監視（ページング/既読） =====
    const handleScroll = useCallback(() => {
      const c = chatContainerRef.current;
      if (!c) return;
      const nearTop = isNearTopForPaging();
      const viewingLatest = isViewingLatest();
      if (!showNotes && hasMore && !isFetching && nearTop && selectedEventId) {
        fetchMessages(selectedEventId, false, offset);
      }
      if (
        !showNotes &&
        hasNewerMessages &&
        !loadingNewerMessages &&
        viewingLatest
      ) {
        loadNewerMessages();
      }
      // §3.6: atLatest でも ANCHOR 窓(hasNewerMessages=true)の間は既読化しない。
      if (!showNotes && viewingLatest && !hasNewerMessages) {
        markLatestRead();
        setShowNewMsgBanner(false);
      }
    }, [
      fetchMessages,
      hasMore,
      hasNewerMessages,
      isFetching,
      isNearTopForPaging,
      isViewingLatest,
      loadNewerMessages,
      loadingNewerMessages,
      markLatestRead,
      offset,
      selectedEventId,
      showNotes,
    ]);

    useEffect(() => {
      const c = chatContainerRef.current;
      if (c) {
        c.addEventListener('scroll', handleScroll);
        return () => c.removeEventListener('scroll', handleScroll);
      }
    }, [handleScroll]);

      // ===== 新着メッセージ・既読更新（socket） =====
      useEffect(() => {
        // §3.3 RECONNECT: 再接続時に開いているイベントチャットを最新へ再同期(silent)。
        // handleNewEventMessage の stale フォールバックと同じ fetchNewMessagesRef 経由の
        // 差分取得を再利用する。未選択なら何もしない。
        const handleConnect = () => {
          if (!selectedEventIdRef.current) return;
          const fetchNewMessagesLatest = fetchNewMessagesRef.current;
          if (!fetchNewMessagesLatest) return;
          fetchNewMessagesLatest()
            .then(() => {
              lastDiffFetchRef.current = Date.now();
            })
            .catch(() => {});
        };

        const handleNewEventMessage = (payload = {}) => {
          const { eventId, threadId } = payload;
          if (!eventId && !threadId) return;
          refreshPendingMentionTargets();
          const currentSelected = selectedEventIdRef.current;
          const selectedThreadId =
            selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const incomingThreadId = threadId || resolveThreadIdByEvent(eventId);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            incomingThreadId &&
            selectedThreadId === incomingThreadId
        );
        const targetEventId =
          (sameThreadAsSelected ? currentSelected : eventId) || currentSelected || eventId;
          const currentMessages = messagesRef.current;
          const isSearchModeNow = isSearchModeRef.current;
          if (targetEventId && (eventId === currentSelected || sameThreadAsSelected)) {
            const realtimeMessage = buildRealtimeEventTextMessage(payload, targetEventId);
            if (realtimeMessage) {
              const wasAtBottom = isViewingLatest();
              const alreadyKnown = messageIdsRef.current.has(realtimeMessage.id);
              if (!alreadyKnown) {
                messageIdsRef.current.add(realtimeMessage.id);
              }
              // 経路①: enriched payload の直接 merge。§3.3 SOCKET_MSG
              setMessages((prev) => mergeMessages(prev, [realtimeMessage]));
              if (!alreadyKnown) {
                setOffset((prev) => prev + 1);
              }
              lastDiffFetchRef.current = Date.now();

              const showNotesNow = showNotesRef.current;
              const unreadPatch = showNotesNow || !chatTopics.canClearParentUnread() ? {} : { unreadCount: 0 };
              const sidebarBase = realtimeMessage.content || 'メッセージを送信しました';
              const sidebarPreview = sidebarBase.length > 50 ? `${sidebarBase.slice(0, 50)}…` : sidebarBase;
              const sidebarTime = nowJstDbString();
              if (isSearchModeNow) {
                setEvents(prev =>
                  prev.map(e =>
                    e.event_uuid === targetEventId
                      ? {
                          ...e,
                          last_message_preview: sidebarPreview,
                          last_message_time: sidebarTime,
                          last_message_format: realtimeMessage.message_format,
                          ...unreadPatch,
                        }
                      : e
                  )
                );
              } else {
                setEvents(prev =>
                  bumpEventToTop(prev, targetEventId, {
                    last_message_preview: sidebarPreview,
                    last_message_time: sidebarTime,
                    last_message_format: realtimeMessage.message_format,
                    ...unreadPatch,
                  })
                );
              }

              if (!showNotesNow && wasAtBottom) {
                postEventReadReceipt(targetEventId, realtimeMessage.id).catch(() => {});
                if (chatTopics.canClearParentUnread()) setEvents(prev => prev.map(e => (e.event_uuid === targetEventId ? { ...e, unreadCount: 0 } : e)));
                if (chatTopics.canClearParentUnread()) setUnreadMap(prev => ({ ...prev, [targetEventId]: 0 }));
                requestAnimationFrame(() => {
                  if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                  }
                });
              } else if (!showNotesNow && !realtimeMessage.isMyArtistMsg) {
                const name = realtimeMessage.message_type === 'admin_message' ? '管理者' : 'メンバー';
                const bannerPreview = sidebarBase.length > 40 ? `${sidebarBase.slice(0, 40)}…` : sidebarBase;
                openBanner({ id: realtimeMessage.id, name, part: '', preview: bannerPreview, avatarUrl: '' });
              }


              refreshEventChatUnread();
              if (!isSearchModeRef.current) requestRefreshTop();
              return;
            }
            const stale = Date.now() - lastDiffFetchRef.current > STALE_MS;
          if (stale || currentMessages.length === 0) {
            const fetchMessagesLatest = fetchMessagesRef.current;
            if (fetchMessagesLatest) {
              fetchMessagesLatest(targetEventId, true).then(() => {
                lastDiffFetchRef.current = Date.now();
              });
            }
            return;
          }
          const wasAtBottom = isViewingLatest();

          const fetchNewMessagesLatest = fetchNewMessagesRef.current;
          if (!fetchNewMessagesLatest) return;
          fetchNewMessagesLatest().then((added = []) => {
            lastDiffFetchRef.current = Date.now();
            if (selectedEventIdRef.current !== targetEventId) return;
            const showNotesNow = showNotesRef.current;
            const unreadPatch = showNotesNow || !chatTopics.canClearParentUnread() ? {} : { unreadCount: 0 };
            if (!showNotesNow && wasAtBottom) markLatestRead();

            if (added.length) {
              const lastAdded = added[added.length - 1];
              const isSystemMsg = lastAdded.message_type === 'system_message';
              const isEventCancelled =
                isSystemMsg &&
                (lastAdded.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || lastAdded.message_format === 'event_cancelled');

              const isNote = isNoteMessage(lastAdded);
              const isNoteUpdate = isNoteUpdateMessage(lastAdded);
              const isNoteDelete = isNoteDeleteMessage(lastAdded);
              const noteBannerText =
                isNoteDelete ? '📝 ノート削除' :
                isNoteUpdate ? '📝 ノート更新' : '📝 ノート作成';
              const noteSidebarText =
                isNoteDelete ? 'ノートが削除されました。' :
                isNoteUpdate ? 'ノートが修正されました。' : 'ノートが作成されました。';
              const sidebarBase =
                isEventCancelled ? 'イベントが中止となりました。' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.FILE  ? 'ファイルが届いています。' :
                lastAdded.message_format === ADMIN_MESSAGE_FORMATS.IMAGE ? '画像が届いています。'  :
                isNote ? noteSidebarText :
                (lastAdded.content || '');
              const sidebarPreview = sidebarBase.length > 50 ? `${sidebarBase.slice(0, 50)}…` : sidebarBase;

              if (isSearchModeNow) {
                setEvents(prev =>
                  prev.map(e =>
                    e.event_uuid === targetEventId
                      ? { ...e, last_message_preview: sidebarPreview, last_message_time: lastAdded.created_at || nowJstDbString(), ...unreadPatch }
                      : e
                  )
                );
              } else {
                setEvents(prev =>
                  bumpEventToTop(prev, targetEventId, {
                    last_message_preview: sidebarPreview,
                    last_message_time: lastAdded.created_at || nowJstDbString(),
                    ...unreadPatch,
                  })
                );
              }

              if (!showNotesNow && !wasAtBottom) {
                const isAdminSender = lastAdded.message_type === 'admin_message';
                const name = isSystemMsg
                  ? 'システム'
                  : (isAdminSender
                      ? formatAdminDisplayName({
                          adminDisplayName: lastAdded.admin_display_name || '',
                          fullName: lastAdded.admin_full_name || '',
                          username: lastAdded.admin_username || '',
                          role: lastAdded.admin_role || '',
                          isExternalAccount: lastAdded.admin_is_external_account === true,
                          externalAffiliationName: lastAdded.admin_external_affiliation_name || '',
                          useAsciiParentheses: true,
                        })
                      : lastAdded.artist_name);
                const resolveMainPart = getMainPartLabelRef.current;
                const part = (isAdminSender || isSystemMsg)
                  ? ''
                  : (resolveMainPart ? resolveMainPart(lastAdded.artist_parts) : '');
                const avatarUrl = isSystemMsg ? '' : (isAdminSender
                  ? (lastAdded.admin_profile_picture
                      ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, lastAdded.admin_profile_picture), 'proxy')
                      : '')
                  : (lastAdded.artist_profile_picture
                      ? appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, lastAdded.artist_profile_picture), 'proxy')
                      : ''));
                const bannerText =
                  isEventCancelled ? '🚫 イベント中止' :
                  lastAdded.message_format === ADMIN_MESSAGE_FORMATS.FILE  ? '📄 ファイル' :
                  lastAdded.message_format === ADMIN_MESSAGE_FORMATS.IMAGE ? '🖼️ 画像'  :
                  isNote ? noteBannerText :
                  (lastAdded.content || '');
                const bannerPreview = bannerText.length > 40 ? `${bannerText.slice(0, 40)}…` : bannerText;
                openBanner({ id: lastAdded.id, name, part, preview: bannerPreview, avatarUrl });
              }
            }
          });

          if (selectedEventIdRef.current === targetEventId && !showNotesRef.current) {
            if (chatTopics.canClearParentUnread()) setEvents(prev => prev.map(e => (e.event_uuid === targetEventId ? { ...e, unreadCount: 0 } : e)));
            if (chatTopics.canClearParentUnread()) setUnreadMap(prev => ({ ...prev, [targetEventId]: 0 }));
          }
          if (selectedEventIdRef.current === targetEventId) {
            refreshEventChatUnread();
          }
        } else {
          if (isSearchModeNow) {
            fetchSidebarRow(eventId)
              .then(row => {
                setEvents(prev => prev.map(ev =>
                  ev.event_uuid === row.event_uuid
                    ? { ...ev, last_message_preview: row.last_message_preview, last_message_time: row.last_message_time, last_message_format: row.last_message_format ?? ev.last_message_format }
                    : ev
                ));
              })
              .catch(() => {});
          } else {
            fetchSidebarRow(eventId)
              .then(row => upsertEventRowAtTop(row))
              .catch(async () => {
                const comp = await getEventComposite(eventId);
                upsertEventRowAtTop({
                  event_uuid: eventId,
                  name: comp.name || '(イベント)',
                  performance_type: comp.performance_type,
                  flyer_front_url: comp.flyer_front_url,
                  last_message_preview: '新着メッセージ',
                  last_message_time: nowJstDbString(),
                  unreadCount: 1,
                });
              })
              .finally(() => {
                refreshEventChatUnread();
                requestRefreshTop();
              });
          }
        }
      };

      const handleReadUpdated = (payload = {}) => {
        const { eventId, threadId, readMessageIds, read_message_ids: readMessageIdsSnake } = payload;
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (eventId !== currentSelected && !sameThreadAsSelected) return;
        const ids = Array.isArray(readMessageIds) ? readMessageIds : readMessageIdsSnake;
        if (Array.isArray(ids) && ids.length > 0) {
          setMessages((prev) => patchMessageReadCountsByIds(prev, ids, payload, { scope: 'event' }));
          refreshEventChatUnread();
          return;
        }
        if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = setTimeout(() => {
          reconcileRecentMessages();
        }, 150);
        refreshEventChatUnread();
      };

      const handleEventMessageDeleted = ({ eventId, messageId, messageIds, threadId } = {}) => {
        const deletedIds = Array.isArray(messageIds) && messageIds.length
          ? messageIds.map((id) => String(id))
          : (messageId ? [String(messageId)] : []);
        if (!eventId || deletedIds.length === 0) return;
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        const targetEventId =
          (sameThreadAsSelected ? currentSelected : eventId) || currentSelected || eventId;
        if (targetEventId === selectedEventIdRef.current) {
          setMessages((prev) => removeMessages(prev, deletedIds));
        }
        const isSearchModeNow = isSearchModeRef.current;
        fetchSidebarRow(targetEventId)
          .then((row) => {
            if (!row) return;
            if (isSearchModeNow) {
              setEvents(prev => prev.map(ev =>
                ev.event_uuid === row.event_uuid
                  ? { ...ev, last_message_preview: row.last_message_preview, last_message_time: row.last_message_time, last_message_format: row.last_message_format ?? ev.last_message_format }
                  : ev
              ));
            } else {
              upsertEventRowAtTop(row, { moveToTop: false });
            }
          })
          .catch(() => {})
          .finally(() => {
            refreshEventChatUnread();
            if (!isSearchModeRef.current) requestRefreshTop();
          });
      };

      const handleTopologyChanged = ({ groupEventUuids = [] } = {}) => {
        const affected = Array.isArray(groupEventUuids) ? groupEventUuids : [];
        const currentSelected = selectedEventIdRef.current;
        const selectedAffected = currentSelected && affected.includes(currentSelected);
        if (!isSearchModeRef.current) requestRefreshTop();
        if (selectedAffected) {
          fetchSidebarRow(currentSelected)
            .then((row) => {
              if (!row) return;
              setSidebarDetails(row);
              upsertEventRowAtTop(row, { moveToTop: false });
            })
            .catch(() => {});
          const fetchMessagesLatest = fetchMessagesRef.current;
          if (fetchMessagesLatest) {
            fetchMessagesLatest(currentSelected, true).catch(() => {});
          }
          refreshAnnouncements({ silent: true }).catch(() => {});
        }
        refreshEventChatUnread();
      };

      const handleAnnouncementsUpdated = ({ eventId, threadId } = {}) => {
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (String(eventId || '') !== String(currentSelected || '') && !sameThreadAsSelected) return;
        refreshAnnouncements({ silent: true }).catch(() => {});
      };

      const handleReactionUpdated = ({ eventId, threadId, messageId } = {}) => {
        const currentSelected = selectedEventIdRef.current;
        const selectedThreadId =
          selectedThreadIdRef.current || resolveThreadIdByEvent(currentSelected);
        const sameThreadAsSelected = Boolean(
          currentSelected &&
            selectedThreadId &&
            threadId &&
            selectedThreadId === threadId
        );
        if (!messageId) return;
        if (String(eventId || '') !== String(currentSelected || '') && !sameThreadAsSelected) return;
        refreshMessageReactionDetails(messageId).catch(() => {});
      };

      const handleLinkPreviewReady = makeLinkPreviewReadyHandler(setMessages);

      socket.on('connect', handleConnect);
      socket.on('new_event_message', handleNewEventMessage);
      socket.on('read_updated', handleReadUpdated);
      socket.on(SOCKET_EVENTS.EVENT_MESSAGE_DELETED, handleEventMessageDeleted);
      socket.on(SOCKET_EVENTS.EVENT_CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
      socket.on(SOCKET_EVENTS.EVENT_CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
      socket.on(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
      socket.on('event_chat_topology_changed', handleTopologyChanged);
      return () => {
        socket.off('connect', handleConnect);
        socket.off('new_event_message', handleNewEventMessage);
        socket.off('read_updated', handleReadUpdated);
        socket.off(SOCKET_EVENTS.EVENT_MESSAGE_DELETED, handleEventMessageDeleted);
        socket.off(SOCKET_EVENTS.EVENT_CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
        socket.off(SOCKET_EVENTS.EVENT_CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
        socket.off(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
        socket.off('event_chat_topology_changed', handleTopologyChanged);
      };
      }, [
        buildRealtimeEventTextMessage,
        isViewingLatest,
        markLatestRead,
        openBanner,
        postEventReadReceipt,
        reconcileRecentMessages,
        requestRefreshTop,
        refreshPendingMentionTargets,
        refreshEventChatUnread,

        fetchSidebarRow,
        refreshAnnouncements,
        refreshMessageReactionDetails,
      upsertEventRowAtTop,
      getEventComposite,
      bumpEventToTop,
      resolveThreadIdByEvent
    ]);

      useEffect(() => {
        return () =>
          cleanupTimeoutRefs([
            bannerTimeoutRef,
            reconcileTimerRef,

            refreshTopTimerRef,
          ]);
      }, []);

    useEffect(() => {
      if (
        !shouldMarkLatestAsRead({
          selectedEventId,
          messagesLength: messages.length,
          showNotes,
          isViewingLatest,
        })
      ) return;
      markLatestRead();
    }, [selectedEventId, messages.length, showNotes, isViewingLatest, markLatestRead]);

    const shouldShowMessageSourceBadge = (message) => {
      return shouldShowEventMessageSourceBadge({
        message,
        isSelectedGroupUnified,
        selectedEventInfo,
        selectedEventId,
        resolveEventChatMode,
        resolveEventGroupCount,
        unifiedMode: CHAT_MODE_GROUP_UNIFIED,
      });
    };

    const announcementChecks = buildEventAnnouncementChecks({
      isNoteCreateMessage,
      isNoteUpdateMessage,
      isNoteDeleteMessage,
      isTimetableMemoMessage,
      isEventInfoPublishedMessage,
      isEventMembersPublishedMessage,
      isStageSeatingSharedMessage,
      isEventInfoUpdatedMessage,
      isEventFinishedMessage,
      isEventMaterialSharedMessage,
      isEventImagesAddedMessage,
      isTimetableCreatedMessage,
      isTimetableUpdatedMessage,
      isTimetableRuntimeMessage,
      isNoteMessage,
    });

    const buildNoteActionNode = (msg) => {
      const messageNoteId = msg.note_id || null;
      return (
        <EventNoteActionButton
          messageNoteId={messageNoteId}
          onOpen={() => {
            setShowNotes(true);
            notesModeRef.current = 'list';
            window.history.pushState({ artistView: 'notes' }, '');
            if (selectedEventId) saveLast(selectedEventId, 'notes-list');
            if (messageNoteId) {
              window.setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent('artist-notes-open-detail', { detail: { noteId: messageNoteId } })
                );
              }, 0);
            }
          }}
        />
      );
    };

    const buildAlbumActionNode = () => (
      <button
        type="button"
        className="system-message-card__link"
        onClick={() => setShowAlbumModal(true)}
      >
        アルバムを見る
      </button>
    );

    const buildMaterialActionNode = () => (
      <button
        type="button"
        className="system-message-card__link"
        onClick={() => setShowMaterialsModal(true)}
      >
        資料を見る
      </button>
    );

    const buildEventFinishedActionNode = (msg = {}) => {
      const contractArtistId = String(
        msg.event_finished_contract_artist_id ||
        msg.contract_artist_id ||
        msg.contractArtistId ||
        '',
      ).trim();
      const eventId = String(
        msg.source_event_id ||
        msg.sourceEventId ||
        msg.event_uuid ||
        msg.eventUuid ||
        msg.event_id ||
        msg.eventId ||
        selectedEventId ||
        '',
      ).trim();
      const href = contractArtistId
        ? `/artist/workspaces/contracts/${encodeURIComponent(contractArtistId)}`
        : eventId
          ? `/artist/workspaces/contracts/dashboard?event_uuid=${encodeURIComponent(eventId)}`
          : '';
      if (!href) return null;
      return (
        <a className="system-message-card__link" href={href}>
          {contractArtistId ? '自分の契約を見る' : '本契約リストを見る'}
        </a>
      );
    };

    const {
      withAnnouncementScopeTitle,
      getAnnouncementToneClass,
      renderMessageContent,
    } = createEventMessageRenderPipeline(createEventPipelineOptions({
      selectedEventInfo,
      selectedEventId,
      resolveEventChatMode,
      unifiedMode: CHAT_MODE_GROUP_UNIFIED,
      announcementChecks,
      getAnnouncementIconClass,
      linkifyText,
      parseTimetableRuntimePayload: parseEventTimetableRuntimePayload,
      isTimetableMemoMessage,
      resolveTimetableRuntimeVisibility: resolveEventTimetableRuntimeVisibility,
      buildTimetableDetailUrl: ({ selectedEventId: eventId, timetableId }) =>
        eventId && timetableId != null
          ? `/artist/workspaces/event-operations/events/${encodeURIComponent(eventId)}/timetables/${encodeURIComponent(timetableId)}`
          : '',
      messageFormats: ADMIN_MESSAGE_FORMATS,
      messageFilePath: MESSAGE_FILE_PATH,
      isTimetableRuntimeMessage,
      isSimpleAnnouncementMessage,
      isEventFinishedMessage,
      isEventMaterialSharedMessage,
      isEventImagesAddedMessage,
      isNoteMessage,
      isNoteDeleteMessage,
      buildNoteActionNode,
      buildMaterialActionNode,
      buildAlbumActionNode,
      buildEventFinishedActionNode,
      buildMessageImageUrl,
      onOpenImage: (msg) => openImageModal(msg.id),
      onDownload: handleDownload,
      buildAssetUrl,
      ellipsizeFileName,
      getFileIconClass,
      formatExpireDateLabel,
      onMentionClick: handleMentionClick,
      contentRendererOverrides: {
        hideUnsupportedSystemMessages: true,
      },
    }));

    const getMainPartLabel = useCallback((artistParts) => {
      const arr = parsePartIds(artistParts);
      if (!arr.length) return '';
      const meta = partsLookup.get(toNum(arr[0]));
      return meta ? (meta.label || meta.short_name || '') : '';
    }, [partsLookup]);
    useEffect(() => {
      getMainPartLabelRef.current = getMainPartLabel;
    }, [getMainPartLabel]);

    const getMainPartBadgeLabel = useCallback((artistParts) => {
      const arr = parsePartIds(artistParts);
      if (!arr.length) return '';
      const meta = partsLookup.get(toNum(arr[0]));
      return meta ? (meta.short_name || meta.label || '') : '';
    }, [partsLookup]);
    const resolveEventPartLabel = useCallback((artist) => {
      if (!artist) return '';
      const ci = artist.casting_info_id != null ? castingInfoMap.get(String(artist.casting_info_id)) : null;
      const label = normalizePartLabel(
        pickFirstString(ci?.part, artist.partLabel, artist.mainPartLabel, artist.part, artist.mainPart)
      );
      if (label) return label;
      const fallback = normalizePartLabel(getMainPartLabel(artist.artist_parts || artist.parts));
      if (fallback) return fallback;
      return normalizePartLabel(getMainPartBadgeLabel(artist.artist_parts || artist.parts));
    }, [castingInfoMap, getMainPartLabel, getMainPartBadgeLabel]);

    const {
      buildArtistProfilePayload,
      buildAdminProfilePayload,
    } = useMemo(
      () =>
        createEventProfilePayloadBuilders({
          pickFirstString,
          normalizePartLabel,
          getMainPartBadgeLabel,
          normalizeEventUuidList,
        }),
      [getMainPartBadgeLabel]
    );

    const resolveAvatarProfileTarget = useMemo(
      () =>
        createEventAvatarProfileTargetResolver({
          isAnnouncementMessage,
          participantSnapshot,
          adminLookup,
          participantArtistMap,
          buildAdminProfilePayload,
          buildArtistProfilePayload,
          buildParticipantEventMembershipLabel,
          buildParticipantEventPartSummary: (profileSource) => {
            const fallbackPartLabel = resolveEventPartLabel(profileSource) || 'パート未設定';
            return buildParticipantEventPartSummary(profileSource, fallbackPartLabel);
          },
          selectedEventId,
          effectiveGroupEvents,
          isUnifiedEventGroupChat: isSelectedGroupUnified,
          normalizeEventUuidList,
          pickFirstString,
        }),
      [
        adminLookup,
        participantSnapshot,
        participantArtistMap,
        buildAdminProfilePayload,
        buildArtistProfilePayload,
        buildParticipantEventMembershipLabel,
        buildParticipantEventPartSummary,
        resolveEventPartLabel,
        isSelectedGroupUnified,
        selectedEventId,
        effectiveGroupEvents,
      ]
    );

    const handleAvatarClick = useCallback((message) => {
      const target = resolveAvatarProfileTarget(message);
      openProfileTarget(target);
    }, [openProfileTarget, resolveAvatarProfileTarget]);

    const {
      renderAnnouncementAvatarLeft,
      renderAdminAvatarLeft,
      renderArtistAvatarLeft,
    } = createEventAvatarRenderers({
      isNoteMessage,
      getAnnouncementToneClass,
      getAnnouncementIconClass,
      buildAssetUrl,
      appendVariant,
      adminImageRoot: ADMIN_PROFILE_IMAGE_PATH,
      artistImageRoot: ARTIST_PROFILE_IMAGE_PATH,
      personClassName: 'message-avatar',
      clickableClassName: 'message-avatar--clickable',
      initialClassName: 'message-avatar-initial',
      announcementClassName: 'message-avatar announce-avatar',
      resolveArtistFallbackStyle: (message) => {
        if (message?.artist_gender === 'male') return { backgroundColor: 'var(--person-avatar-artist-male-bg)' };
        if (message?.artist_gender === 'female') return { backgroundColor: 'var(--person-avatar-artist-female-bg)' };
        return { backgroundColor: 'var(--person-avatar-artist-unknown-bg)' };
      },
    });

    const formatEventListTimestamp = useCallback(
      (dbString) => sidebarTimeLabelJST(dbString),
      []
    );

    const toggleNotes = useCallback(() => {
      setShowNotes((prev) => {
        const next = !prev;
        if (!prev && next) {
          window.history.pushState({ artistView: 'notes' }, '');
          notesModeRef.current = 'list';
        }
        if (selectedEventId) {
          if (next) saveLast(selectedEventId, 'notes-list');
          else saveLast(selectedEventId, 'chat');
        }
        return next;
      });
    }, [selectedEventId, saveLast]);

    const dropAreaRef = useRef(null);
    const allowedDnd = !!selectedEventId && !fatalAccessError && !isSendLocked && !isReadOnly;
    const uploadImagesBatch = useCallback(async (eventId, files) => {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      if (replyDraft?.messageId) fd.append('replyToMessageId', String(replyDraft.messageId));
      try {
        return await sendEventChatImageMessages(eventId, fd);
      } catch (error) {
        const msg = normalizeServerMessage(error?.message) || statusToDisplayMessage(error?.status, '画像送信に失敗しました。');
        const err = new Error(msg);
        err.status = error?.status;
        throw err;
      }
    }, [replyDraft]);
    const uploadOneFile = useCallback(async (eventId, file) => {
      const fd = new FormData();
      fd.append('eventId', eventId);
      fd.append('file', file);
      if (replyDraft?.messageId) fd.append('replyToMessageId', String(replyDraft.messageId));
      try {
        return await sendEventChatFileMessage(eventId, fd);
      } catch (error) {
        const msg = normalizeServerMessage(error?.message) || statusToDisplayMessage(error?.status, 'ファイル送信に失敗しました。');
        const err = new Error(msg);
        err.status = error?.status;
        throw err;
      }
    }, [replyDraft]);
    const sendDroppedFilesDirect = useCallback(async (incomingFiles) => {
      if (isReadOnly) { toastError('現在メンテナンス中のため送信できません。'); return; }
      if (!selectedEventId) { toastError('イベントを選択してください。'); return; }
      if (fatalAccessError) { toastError('このイベントにはアクセスできません。'); return; }
      if (isSendLocked)     { toastError('イベント終了から1ヵ月経過のため、送信できません。'); return; }

      const plan = buildChatDropPlan(incomingFiles, { maxImages: MAX_IMAGE_COUNT, maxBytes: MAX_FILE_BYTES });
      if (plan.notice) toastError(plan.notice);
      if (!plan.hasSendable) return;

      setIsUploading(true);
      try {
        if (plan.images.length) {
          await uploadImagesBatch(selectedEventId, plan.images);
          updateSidebarAfterSend('画像が届いています。');
        }
        for (const file of plan.files) {
          // eslint-disable-next-line no-await-in-loop -- 送信順を保つため直列に送る
          await uploadOneFile(selectedEventId, file);
        }
        if (plan.files.length) {
          updateSidebarAfterSend('ファイルが届いています。');
        }
        const added = await fetchNewMessages();
        requestAnimationFrame(() => {
          if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        });
        if (added && added.length) {
          markLatestRead();
          if (chatTopics.canClearParentUnread()) setEvents(prev => prev.map(e => (e.event_uuid === selectedEventId ? { ...e, unreadCount: 0 } : e)));
          if (chatTopics.canClearParentUnread()) setUnreadMap(prev => ({ ...prev, [selectedEventId]: 0 }));
        }
        setReplyDraft(null);
      } catch (e) {
        toastError(e?.message || '送信に失敗しました。時間を置いて再度お試しください。');
      } finally {
        setIsUploading(false);
      }
    }, [selectedEventId, fatalAccessError, isReadOnly, isSendLocked, toastError, updateSidebarAfterSend, fetchNewMessages, markLatestRead, uploadImagesBatch, uploadOneFile]);

    const { isDragActive: isDndDragging, overlayRect } = useChatDragDrop({
      areaRef: dropAreaRef,
      highlightRef: dropAreaRef,
      enabled: allowedDnd,
      onDrop: sendDroppedFilesDirect,
      maxBytes: MAX_FILE_BYTES,
      onInvalid: () => {
        if (!selectedEventId)        toastError('イベントを選択してください。');
        else if (fatalAccessError)   toastError('このイベントにはアクセスできません。');
        else if (isSendLocked)       toastError('イベント終了から1ヵ月経過のため、送信できません。');
      },
    });

    const busyActive = (isUploading || isFetching) && !showNotes;
    const composerRef = useRef(null);
  return {
    shouldShowMessageSourceBadge,
    withAnnouncementScopeTitle,
    renderMessageContent,
    getMainPartLabel,
    handleAvatarClick,
    renderAnnouncementAvatarLeft,
    renderAdminAvatarLeft,
    renderArtistAvatarLeft,
    formatEventListTimestamp,
    toggleNotes,
    dropAreaRef,
    allowedDnd,
    isDndDragging,
    overlayRect,
    busyActive,
    composerRef,
  };
}
